import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DatesProvider } from "../../../../../dates/DatesContext";
import BreadcrumbDates from "../../../../../components/core/blocks/BreadcrumbDates";
import { createFileRoute } from "@tanstack/react-router";
import Loading from "../../../../../components/core/blocks/Loading";
import { DevErrorDisplay } from "../../../../../components/core/blocks/DevErrorDisplay";
import { useDates } from "../../../../../dates/DatesContext";
import { useFetch } from "../../../../../hooks/api";
import { Row, Col, Nav, Card } from "react-bootstrap";
import getMetricSpec from "../../../../../modules/merchandising/utils/metrics";
import MetricsCard from "../../../../../components/custom/merchandise/MetricsCard";
import NoImage from "../../../../../assets/images/utility/no_image.png";
import { getURLPrefix } from "../../../../../utils/getUrlPrefix";
import { PricingRulesNav } from "../../../../../modules/pricingv2/utils";
import { DemandChartsSection } from "../../../../../modules/pricingv2/cards/DemandTrend";
import { ConfigurationSection } from "../../../../../modules/pricingv2/cards/Configuration";
import RelationshipSection from "../../../../../modules/pricingv2/cards/Relationships";
import { ProductsTab } from "../../../../../modules/pricingv2/cards/ProductsTab";
import PriceHistorySection from "../../../../../modules/pricingv2/cards/PriceHistory";
import CancelRateChart from "../../../../../modules/pricingv2/charts/CancelRateChart";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../../../utils/dashboardLinks";
import useCategories from "../../../../../hooks/useCategories";
import usePricingPlans from "../../../../../hooks/usePricingPlans";
import useQueryParams from "../../../../../hooks/useQueryParams";
import { InlinePrograms } from "../../../../../components/custom/programs/InlinePrograms";
import ProgramLog from "../../../../../components/custom/programs/ProgramLog";
import PlanHeader from "../../../../../modules/pricingv2/components/PlanHeader";
import ConfirmationSection from "../../../../../modules/pricingv2/cards/Confirmation";
import { FaCheckCircle } from "react-icons/fa";
import { useAppSelector } from "../../../../../redux/hooks";
import { useModuleEnabled } from "../../../../../redux/selectors/modules";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { usePricingPlan } from "../../../../../modules/pricingv2/api/usePricingPlan";
import TrellisNavLink from "../../../../../components/core/basic/NavLink";
import { PricingPlanHistoryTab } from "../../../../../modules/pricingv2/components/PricingPlan/PricingPlanHistoryTab";
import { keepPreviousData } from "@tanstack/react-query";
import PriceDetails from "../../../../../modules/merchandising/components/PriceDetails";
import { trellisPalette } from "../../../../../components/custom/analytics/palettes";
import { cut } from "../../../../../utils/formatText";
import { MetricCards } from "../../../../../modules/pricingv2/components/dashboard/MetricCards";
import { IoTime } from "react-icons/io5";
import { MetricDebug } from "../../../../../modules/pricingv2/components/MetricDebug";
import CompetitorsSection from "../../../../../modules/pricingv2/cards/Competitors";

const URL_PREFIX = getURLPrefix();

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  CATEGORY: "category",
  PLAN: "plan",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;

  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/pricing/dashboard/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PLAN:
      to = `/user/pricing/plan/${resourceId}`;
      break;
    default:
      to = `/user/pricing/dashboard`;
  }

  return to;
};

const PriceSection = ({ pricingPlan, marketPlace, isPlanLoading, start, end, group, data }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [tab, setTab] = useState("price_history");

  const { data: historyData, isLoading: isHistoryDataLoading } = useFetch(
    ["price_history", pricingPlan],
    `/pricing/price_history`,
    {
      pricing_plan_id: pricingPlan.id,
    },
    {
      select: (d) => d?.data,
      keepPreviousData: false,
    },
  );

  return (
    <Row>
      <Col xs={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="price_history" className="text-start">
            {t(($) => $.pricingv2["Price History"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="ad_affect" className="text-start">
            {t(($) => $.pricingv2["Advertising Effectiveness"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="price-changes" className="text-start">
            {t(($) => $.pricingv2["Price Changes"])}
          </TrellisNavLink>
          {!isHistoryDataLoading && (
            <TrellisNavLink eventKey="competitors" className="text-start">
              {t(($) => $.pricingv2["Competitors"])}
            </TrellisNavLink>
          )}
          {!isHistoryDataLoading && (
            <TrellisNavLink eventKey="details" className="text-start">
              {t(($) => $.pricingv2["Amazon Price Details"])}
            </TrellisNavLink>
          )}
        </Nav>
      </Col>
      <Col xs={10}>
        {tab === "details" &&
          !isHistoryDataLoading &&
          Object.values(historyData).map((hd) => (
            <div key={hd.product_id}>
              <PriceDetails
                productId={hd.product_id}
                marketPlace={marketPlace}
                isHistoryDataLoading={isHistoryDataLoading}
                historyData={historyData}
                showProductDetails={Object.values(historyData).length > 1}
              />
            </div>
          ))}

        {!isHistoryDataLoading && tab === "competitors" && (
          <div>
            {Object.values(historyData).map((hd) => (
              <div key={hd.sku}>
                {Object.values(historyData).length > 1 && (
                  <div className="fs-4 fw-bold px-3">
                    {hd.sku} - {hd.product_title}
                  </div>
                )}
                <CompetitorsSection productId={hd.product_id} marketPlace={marketPlace} />
              </div>
            ))}
          </div>
        )}

        {tab === "price_history" && (
          <>
            <Card style={{ borderRadius: "0.5rem" }}>
              <Card.Body className="p-4">
                <DemandChartsSection
                  pricingProduct={pricingPlan}
                  product={null}
                  isPricingProductDataLoading={isPlanLoading}
                  data={data}
                />
              </Card.Body>
            </Card>

            <PriceHistorySection
              pricingProduct={pricingPlan}
              marketPlace={marketPlace}
              isPricingProductDataLoading={isPlanLoading}
              isLoading={false}
            />
          </>
        )}
        {tab === "ad_affect" && (
          <PriceHistorySection
            pricingProduct={pricingPlan}
            marketPlace={marketPlace}
            isPricingProductDataLoading={isPlanLoading}
            isLoading={false}
            advertising={true}
          />
        )}

        {tab === "cancel_rate" && (
          <CancelRateChart pricingPlan={pricingPlan} marketPlace={marketPlace} start={start} end={end} group={group} />
        )}

        {tab === "price-changes" && <ConfirmationSection pricingPlan={pricingPlan} marketPlace={marketPlace} />}
      </Col>
    </Row>
  );
};

const ConfigurationCard = ({ pricingPlan, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const [tab, setTab] = useState("configuration");
  return (
    <Row>
      <Col xs={1}>
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
          className="flex-column text-start"
        >
          <TrellisNavLink eventKey="configuration" className="text-start">
            {t(($) => $.pricingv2["Configuration"])}
          </TrellisNavLink>
          <TrellisNavLink eventKey="history" className="text-start">
            {t(($) => $.pricingv2["History"])}
          </TrellisNavLink>
        </Nav>
      </Col>
      <Col xs={11}>
        {tab === "configuration" && <ConfigurationSection pricingPlan={pricingPlan} marketPlace={marketPlace} />}
        {tab === "history" && <PricingPlanHistoryTab pricingPlan={pricingPlan} marketPlace={marketPlace} />}
      </Col>
    </Row>
  );
};

const MetricSelector = ({ hideChart, setHideChart }) => {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <Row>
      <Col xs={12} className="mb-4 text-start">
        <span className="fs-5 text-center" style={{ cursor: "pointer" }} onClick={() => setHideChart(!hideChart)}>
          <span className="fs-4">
            <IoTime />
          </span>{" "}
          <span>
            {hideChart ?
              t(($) => $.pricingv2["Show Timeline Metrics"])
            : t(($) => $.pricingv2["Hide Timeline Metrics"])}
          </span>
        </span>
      </Col>
    </Row>
  );
};

const PlanCard = ({
  planId,
  marketPlace,
  user,
  additionalTabs,
  initialTab,
  data,
  dashboardData,
  isLoading,
  dashboardIsLoading,
  pricingPlan,
  isPlanLoading,
  group,
  start,
  end,
  hideChart,
  setHideChart,
}) => {
  const { t } = useTranslation(["pricingv2"]);
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");
  const hasPriceVariationModule = useModuleEnabled("PRICE_VARIATION");
  const [tab, setTab] = useQueryParams("tab", initialTab ?? "price_history");
  const overviewMetrics = useMemo(
    () => [
      getMetricSpec("profit"),
      getMetricSpec("margin"),
      getMetricSpec("sales"),
      getMetricSpec("units"),
      getMetricSpec("price"),
      getMetricSpec("ad_spend"),
    ],
    [],
  );

  const image = `${URL_PREFIX}/api/productimage/?pricingplan=${planId}`;
  const planName = pricingPlan?.plan_name;

  const SummaryHeader = (
    <div style={{ display: "flex", width: "100%" }} className="px-2">
      <div style={{ width: "120px", paddingRight: "20px" }}>
        <div
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            flexShrink: 0,
            width: 80,
            height: 80,
            marginRight: "10px",
            background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
          }}
          draggable="false"
        />
      </div>

      <Row style={{ flex: 1, minWidth: 0 }}>
        <h3
          style={{
            marginBottom: "0.2rem",
            marginTop: "0",
          }}
          className="fs-2 fw-100 text-truncate"
        >
          {cut(planName, 75)}
        </h3>
        <Col xs={12} className="text-center">
          {pricingPlan && (
            <PlanHeader
              pricingPlan={pricingPlan}
              user={user}
              marketPlace={marketPlace}
              forecastBucket={dashboardData?.bucket}
              dashboardData={dashboardData}
            />
          )}
        </Col>
        <Col xs={12}></Col>
      </Row>
    </div>
  );

  return (
    <>
      {isLoading || isPlanLoading || dashboardIsLoading ?
        <Loading height={"300px"} fullPage={false} />
      : <Row>
          <Col xs={12} className="mb-4 fs-standard">
            <MetricsCard
              initialSelectedMetric={["total_units_ordered"]}
              titleElement={SummaryHeader}
              metrics={overviewMetrics}
              totals={
                hideChart ? undefined : (
                  {
                    data: data?.total,
                    loading: isLoading,
                  }
                )
              }
              timeline={
                hideChart ? undefined : (
                  {
                    data: data?.date || [],
                    loading: isLoading,
                  }
                )
              }
              group={group}
              showMetricsSelector={!hideChart}
              id={"productDashboard"}
              context="pricingPlan"
              byline={
                <div className="text-start pt-5 ps-5">
                  <MetricSelector hideChart={hideChart} setHideChart={setHideChart} />
                </div>
              }
            >
              {hideChart && dashboardData && (
                <MetricCards
                  dashboardData={dashboardData}
                  isLoading={dashboardIsLoading}
                  planType="margin"
                  marketPlace={marketPlace.marketPlace}
                  pricingPlan={pricingPlan}
                  fetching={false}
                  singlePlan
                />
              )}
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    {additionalTabs?.map((additionalTab) => (
                      <TrellisNavLink eventKey={additionalTab.title?.toLowerCase()} key={additionalTab.title}>
                        {additionalTab.title}
                      </TrellisNavLink>
                    ))}

                    <TrellisNavLink
                      eventKey="price_history"
                      visible={pricingPlan && pricingPlan.plan_type !== "same_strategy"}
                    >
                      {t(($) => $.pricingv2["Prices"])}
                    </TrellisNavLink>

                    <TrellisNavLink eventKey="products">{t(($) => $.pricingv2["Products"])}</TrellisNavLink>

                    <TrellisNavLink eventKey="configuration">{t(($) => $.pricingv2["Configuration"])}</TrellisNavLink>
                    <TrellisNavLink
                      eventKey="relationships"
                      visible={pricingPlan && pricingPlan.plan_type !== "same_strategy"}
                    >
                      {t(($) => $.pricingv2["Relationships"])}
                      {pricingPlan.relationship && (
                        <small>
                          {" "}
                          <FaCheckCircle />
                        </small>
                      )}
                    </TrellisNavLink>

                    <PricingRulesNav pricingPlan={pricingPlan} hasModule={hasPriceDebugModule} />
                    <TrellisNavLink eventKey="data_debug" visible={hasPriceDebugModule}>
                      {t(($) => $.pricingv2["Data Analysis (Debug)"])}
                    </TrellisNavLink>
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  {additionalTabs?.map((additionalTab) => (
                    <Fragment key={additionalTab.title}>
                      {tab === additionalTab.title?.toLowerCase() && additionalTab.content}
                    </Fragment>
                  ))}
                  {tab === "products" && (
                    <ProductsTab marketPlace={marketPlace} user={user} pricingPlan={pricingPlan} />
                  )}
                  {tab === "relationships" && (
                    <RelationshipSection
                      pricingPlan={pricingPlan}
                      marketPlace={marketPlace}
                      hasVariation={hasPriceVariationModule}
                    />
                  )}

                  {tab === "configuration" && <ConfigurationCard pricingPlan={pricingPlan} marketPlace={marketPlace} />}
                  {tab === "price_history" && (
                    <PriceSection
                      pricingPlan={pricingPlan}
                      marketPlace={marketPlace}
                      isPlanLoading={isPlanLoading}
                      isLoading={false}
                      start={start}
                      end={end}
                      group={group}
                      data={data}
                    />
                  )}

                  {tab === "rules_engine" && (
                    <>
                      <InlinePrograms pricingPlan={pricingPlan} />

                      <div
                        className="pt-5"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: "10px",
                        }}
                      >
                        <div>
                          <strong>{t(($) => $.pricingv2["Internal Min Price"])}</strong>:{" "}
                          {pricingPlan?.internal_min_price}
                        </div>
                        <div>
                          <strong>{t(($) => $.pricingv2["Internal Max Price"])}</strong>:{" "}
                          {pricingPlan?.internal_max_price}
                        </div>
                        <div>
                          <strong>{t(($) => $.pricingv2["Current Price"])}</strong>: {pricingPlan?.current_price}
                        </div>
                        <div>
                          <strong>{t(($) => $.pricingv2["Best Price"])}</strong>: {pricingPlan?.best_price}
                        </div>
                      </div>

                      <ProgramLog
                        pricingPlanId={pricingPlan.id}
                        marketPlace={marketPlace}
                        title={t(($) => $.pricingv2["Dynamic Pricing"])}
                      />
                    </>
                  )}

                  {tab === "data_debug" && (
                    <MetricDebug
                      dashboardData={dashboardData}
                      filters={{
                        start_date: start.format("YYYY-MM-DD"),
                        end_date: end.format("YYYY-MM-DD"),
                        mp: marketPlace.marketPlace,
                        plan_id: planId,
                        csv: "true",
                      }}
                    />
                  )}
                </Col>
              </Row>
            </MetricsCard>
          </Col>
        </Row>
      }
    </>
  );
};

const Plan = () => {
  const { t } = useTranslation(["pricingv2"]);
  const { plan: planId } = Route.useParams();
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const { start, end, preStart, preEnd, group } = useDates();
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");
  const [hideChart, setHideChart] = useState(true);

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "plan_id",
    empty: true,
  };

  const { data, isLoading } = useFetch(
    ["pricingplan_report", marketPlace.marketPlace, start, end, preStart, preEnd, planId],
    `/api/data_report/pricingplan_report?plan_id=${planId}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !hideChart,
    },
  );

  const { data: dashboardData, isLoading: dashboardIsLoading } = useFetch(
    ["pricingplan_report_v4", marketPlace.marketPlace, start, end, preStart, preEnd, planId],
    `/pricing/dashboard_v4_stats?plan_id=${planId}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const pricingPlanQuery = usePricingPlan({ id: planId });

  const categoryId = pricingPlanQuery.data?.category;

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "pricing/dashboard",
  );

  const { currentPlanDetails, plans: categoryPlans } = usePricingPlans(categoryId, planId);

  // Breadcrumbs
  const breadCrumbItems = useMemo(() => {
    return [
      AGENCY_DASHBOARD_BREADCRUMB_SPEC,
      {
        name: t(($) => $.pricingv2["Pricing Dashboard"]),
        href: getDashboardTo({
          resourceId: null,
          type: DASHBOARD_TYPES.ROOT,
        }),
        dropdownLinkOptions: DASHBOARD_LINKS,
        unsorted: true,
        searchable: false,
        dropdownSubtitle: t(($) => $.pricingv2["Dashboards"]),
      },
      {
        name: categoryDetails?.leaf_category_name,
        href: categoryDetails.href,
        dropdownLinkOptions: categoryData,
        searchable: true,
        searchPlaceholderText: t(($) => $.pricingv2["Search categories..."]),
        dropdownSubtitle: t(($) => $.pricingv2["Categories"]),
      },
      {
        name: currentPlanDetails?.plan_name,
        href: currentPlanDetails?.href,
        dropdownLinkOptions: categoryPlans,
        searchable: true,
        searchPlaceholderText: t(($) => $.pricingv2["Search pricing plans..."]),
        dropdownSubtitle: t(($) => $.pricingv2["Pricing Plans"]),
      },
    ];
  }, [categoryDetails, categoryData, categoryPlans, currentPlanDetails, t]);

  if (
    pricingPlanQuery.error &&
    "message" in pricingPlanQuery.error &&
    pricingPlanQuery.error.message === "Request failed with status code 404"
  ) {
    return (
      <>
        <BreadcrumbDates
          items={breadCrumbItems}
          hideCompare={hideChart}
          hideGroupBy={hideChart}
          hideIncludeCurrentPeriod={hideChart}
        />
        <PageContainer>
          <h1>{t(($) => $.pricingv2["Plan not found."])}</h1>
        </PageContainer>
      </>
    );
  }

  if (pricingPlanQuery.isLoading) {
    return <Loading fullPage />;
  }

  if (!pricingPlanQuery.isSuccess) {
    return (
      <>
        <BreadcrumbDates
          items={breadCrumbItems}
          hideCompare={hideChart}
          hideGroupBy={hideChart}
          hideIncludeCurrentPeriod={hideChart}
        />
        <PageContainer>
          <DevErrorDisplay
            error={pricingPlanQuery.error}
            userMessage={t(($) => $.pricingv2["Failed to load pricing plan."])}
          />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <BreadcrumbDates
        items={breadCrumbItems}
        hideCompare={hideChart}
        hideGroupBy={hideChart}
        hideIncludeCurrentPeriod={hideChart}
      />
      <PageContainer withBreadcrumb>
        {hasPriceDebugModule && (
          <Row>
            <Col xs={12} className="mb-4 p-0 m-0">
              {!pricingPlanQuery.isLoading && pricingPlanQuery.data.status_reason === "Price Mismatch" && (
                <div
                  style={{
                    border: "1px solid #c0c0c0",
                    backgroundColor: trellisPalette[2],
                    color: "#fff",
                    marginLeft: "-5px",
                    marginTop: "-10px",
                  }}
                  className="p-3 fs-4 text-center"
                >
                  {t(
                    ($) =>
                      $.pricingv2[
                        "There seems to be a price mismatch between the pricing plan and the price on Amazon."
                      ],
                  )}
                </div>
              )}
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12} className="mb-4 px-3">
            <PlanCard
              planId={planId}
              marketPlace={marketPlace}
              user={user}
              channel={channel}
              data={data}
              dashboardData={dashboardData}
              isLoading={isLoading}
              dashboardIsLoading={dashboardIsLoading}
              group={group}
              pricingPlan={pricingPlanQuery.data}
              isPlanLoading={pricingPlanQuery.isLoading}
              start={start}
              end={end}
              hideChart={hideChart}
              setHideChart={setHideChart}
            />
          </Col>
        </Row>
      </PageContainer>
    </>
  );
};

const PricingPlanPageV2 = () => {
  return (
    <DatesProvider>
      <Plan />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/pricing/plan/$plan")({
  component: PricingPlanPageV2,
});
